import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Input } from "antd";
import Rangepicker from "./Rangepicker";
import RedpinIcon from "../../../assets/location_icon_12.PNG";
import Inputbox from "../../../components/formcontrols/normal/InputBox";
import Tint from "../../../utility/ImageTint/Tint";
import deleteimg from "../../../assets/delete-transformed.png";

function fetchWithTimeout(resource, options = {}, timeout = 5000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), timeout);

    fetch(resource, options)
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

const getLocationName = async (coordString) => {
  const [lat, lon] = coordString.split(",").map((x) => x.trim());

  if (isNaN(lat) || isNaN(lon)) throw new Error("Invalid coords");

  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`;

  const res = await fetchWithTimeout(url);
  const data = await res.json();

  if (!data?.display_name) throw new Error("No data");

  return data.display_name;
};

const LocationRangePickerModal = ({
  isOpen,
  onClose,
  selectedLocation = {},
  onDeleteLocation,
  onEditLocation,
  orggpscoordinates = [],
  setOrggpscoordinates,
  grouplist = [],
}) => {
  const [locationName, setLocationName] = useState({
    status: "loading",
    value: "",
  });

  const [locationNameColor, setLocationNameColor] = useState("black");
  const [locationnameget, setLocationNameGet] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [currentRange, setCurrentRange] = useState(25);
  const [groupSearch, setGroupSearch] = useState("");

  const getNextLocationId = () => {
    if (!orggpscoordinates.length) return "1";

    const maxId = Math.max(
      ...orggpscoordinates.map((l) => Number(l.locationid) || 0)
    );

    return String(maxId + 1);
  };

  useEffect(() => {
    if (!isOpen) return;

    setLocationNameGet(selectedLocation?.locationname || "");
    setLocationNameColor("black");
  }, [isOpen, selectedLocation?.locationid]);

  useEffect(() => {
    if (!isOpen) return;

    const groupids = selectedLocation?.groupids ?? "-1";

    if (groupids === -1 || groupids === "-1") {
      setIsAllSelected(true);
      setSelectedGroups([-1]);
    } else {
      setIsAllSelected(false);

      const parsed = String(groupids)
        .split(",")
        .map((id) => Number(id.trim()))
        .filter((id) => !Number.isNaN(id));

      setSelectedGroups(parsed);
    }
  }, [isOpen, selectedLocation?.locationid]);

  useEffect(() => {
    if (!isOpen) return;

    setCurrentRange(Number(selectedLocation?.range || 25));
  }, [isOpen, selectedLocation?.locationid]);

  useEffect(() => {
    if (!isOpen) return;

    setGroupSearch("");
  }, [isOpen, selectedLocation?.locationid]);

  useEffect(() => {
    if (!isOpen || !selectedLocation?.lat || !selectedLocation?.lng) return;

    let ignore = false;

    const fetchLocation = async () => {
      setLocationName({ status: "loading", value: "" });

      try {
        const name = await getLocationName(
          `${selectedLocation.lat},${selectedLocation.lng}`
        );

        if (!ignore) {
          setLocationName({
            status: "success",
            value: name,
          });
        }
      } catch (err) {
        if (!ignore) {
          setLocationName({
            status: "error",
            value: `${selectedLocation.lat},${selectedLocation.lng}`,
          });
        }
      }
    };

    fetchLocation();

    return () => {
      ignore = true;
    };
  }, [
    isOpen,
    selectedLocation?.locationid,
    selectedLocation?.lat,
    selectedLocation?.lng,
  ]);

  const saveAndClose = () => {
    if (!locationnameget?.trim()) {
      setLocationNameColor("red");
      return;
    }

    const isNew =
      selectedLocation?.locationid === undefined ||
      selectedLocation?.locationid === null ||
      selectedLocation?.locationid === "";

    const updatedData = {
      ...selectedLocation,
      locationname: locationnameget.trim(),
      range: currentRange || 25,
      groupids: isAllSelected ? "-1" : selectedGroups.join(","),
    };

    let updatedCoords;

    if (isNew) {
      updatedCoords = [
        ...orggpscoordinates,
        {
          ...updatedData,
          locationid: getNextLocationId(),
        },
      ];
    } else {
      updatedCoords = orggpscoordinates.map((loc) =>
        String(loc.locationid) === String(selectedLocation.locationid)
          ? {
              ...loc,
              ...updatedData,
              locationid: loc.locationid,
            }
          : loc
      );
    }

    setOrggpscoordinates(updatedCoords);
    onClose();
  };

  const handleRangeChange = (range) => {
    setCurrentRange(range);
  };

  const handleCheckboxChange = (e, applgroupid) => {
    let updated = [...selectedGroups];

    if (applgroupid === -1) {
      setIsAllSelected(e.target.checked);
      updated = e.target.checked ? [-1] : [];
    } else {
      if (e.target.checked) {
        setIsAllSelected(false);
        updated.push(applgroupid);
      } else {
        updated = updated.filter((id) => id !== applgroupid);
      }

      if (updated.includes(-1)) {
        updated = updated.filter((id) => id !== -1);
      }
    }

    setSelectedGroups(updated);
  };

  const getDisplayText = () => {
    if (locationName.status === "loading") return "Fetching location...";
    if (locationName.status === "error") return locationName.value;
    return locationName.value;
  };

  const filteredGroupList = groupSearch.trim()
    ? grouplist.filter((res) =>
        (res.applgroupname || "")
          .toLowerCase()
          .includes(groupSearch.trim().toLowerCase())
      )
    : grouplist;

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static" size="lg" style={{ backgroundColor: "rgba(0,0,0,0.85)" }} >
      <Modal.Body style={{ padding: 0, borderRadius: 18, overflow: "hidden" }}>
        <div style={{ padding: 18, background: "#fafafa" }}>
          <div className="row d-flex align-items-start">
            <div className="col-9">
              <Inputbox
                wordboundation={{ limit: 50, id: "locationname" }}
                htmlFor="locationname"
                label="LOCATION NAME"
                type="text"
                labelStyle={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: locationNameColor,
                }}
                placeholder="Enter location name"
                value={locationnameget}
                inputStyle={{ paddingLeft: "0px", fontSize: "14px" ,background: "#fafafa"}}
                onChange={(e) => {
                  setLocationNameGet(e.target.value);
                  setLocationNameColor("black");
                }}
                required={true}
              />
            </div>

            <div
              className="col-3 d-flex align-items-start"
              style={{
                padding: "14px 16px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "start",
                gap: 8,
                fontWeight: 600,
              }}
            >
              <IconButton size="small" onClick={onDeleteLocation}>
                <img
                  src={deleteimg}
                  alt="delete"
                  style={{ width: "20px", filter: Tint("#ff0000") }}
                />
              </IconButton>

              <button
                onClick={saveAndClose}
                style={{
                  padding: "6px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: "#29b6f6",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                OK
              </button>
            </div>
          </div>

          <div
            onClick={onEditLocation}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              borderRadius: 14,
              background: "#fff",
              cursor: "pointer",
            }}
          >
            <img
              src={RedpinIcon}
              alt="location"
              style={{ width: 18, filter: Tint("#ff0000") }}
            />

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 400,background: "#fafafa" }}>
                {getDisplayText()}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              Range (meters)
            </div>

            <div style={{ padding: 12, paddingTop: "0px", borderRadius: 12 }}>
              <Rangepicker
                key={selectedLocation?.locationid || "new"}
                default={currentRange}
                value={currentRange}
                onChange={handleRangeChange}
              />
            </div>
          </div>

          <div style={{ marginTop: 6 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>Groups</div>
<div style={{width:"180px"}}>
{grouplist.length > 0 && (
                <Input
                  allowClear
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  placeholder="Search"
                  style={{
                    fontSize: 13,
                    borderRadius: 8,
                    width: 180,
                  }}
                />
              )}
</div>
              
            </div>

            {grouplist.length > 0 ? (
              filteredGroupList.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "2px 6px",
                    borderRadius: 12,
                    maxHeight: 180,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {filteredGroupList.map((res) => (

                    <FormControlLabel
                      key={res.applgroupid}
                      control={
                        <Checkbox
                          checked={
                            res.applgroupid === -1
                              ? isAllSelected
                              : selectedGroups.includes(res.applgroupid)
                          }
                          onChange={(e) =>
                            handleCheckboxChange(e, res.applgroupid)
                          }
                          sx={{
                            color: "#29b6f6",
                            "&.Mui-checked": { color: "#29b6f6" },
                          }}
                        />
                      }
                      label={res.applgroupname}
                      sx={{
                        margin: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: 13,
                          color: "#444",
                        },
                      }}
                    />

                  ))}
                </div>
              ) : (
                <div style={{ color: "#999", fontSize: 13 }}>
                  No groups match "{groupSearch}"
                </div>
              )
            ) : (
              <div style={{ color: "#999", fontSize: 13 }}>No Group List</div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LocationRangePickerModal;
